/* eslint-disable object-curly-spacing */ 
/* eslint-disable no-undef */ 
/* eslint-disable own-handles */ 
/* eslint-disable no-innerhtml-assign */ 
/* eslint-disable consistent-method-names */ 
/* eslint-disable no-jsdoc */ 
/**
 * The MiniMap allows you to show a smaller map with low zoom level at a corner of the main map.
 *
 * It is based on [Leaflet-MiniMap plugin](https://github.com/Norkart/Leaflet-MiniMap).
 *
 * The default plugin functionality is integrated with TomTom SDK.
 * 
 * If you want to use vector source in the MiniMap the basePath must be specified. 
 * Keep this in mind when you switch from raster to vector source, for example when opening our examples in CodePen, 
 * otherwise the MiniMap will not render properly.
 *
 * @example
 * ```javascript
 * tomtom.key("<your-tomtom-API-key>");
 * var miniMapOptions = {height: 90, width: 120};
 * var map = new L.Map("map");
 * var miniMapControl = new L.TomTomMinimap(miniMapOptions);
 * miniMapControl.addTo(map);
 * ```
 *
 * @example
 * ```javascript
 * var widget = new L.TomTomMinimap({
 *   height: 90,
 *   width: 120,
 *   // zoom difference between the main map and minimaps (defaults to -8)
 *   zoomLevelOffset: -8,
 *
 *   // below options have default values:
 *   toggleDisplay: true,     // show a button to hide/show mini-map
 *   zoomAnimation: true,     // animate zoom changes
 *   autoToggleDisplay: true, // hide mini-map automatically if it's not needed
 *   mapOptions: {
 *     source: ["raster", "vector"],
 *     minZoom: 2, // min zoom of mini-map
 *     maxZoom: 4  // max zoom of mini-map
 *     basePath: 'basePath/sdk' // used for vector maps
 *   }
 * });
 * widget.addTo(map);
 * ```
 *
 * @class TomTomMinimap
 * @namespace L
 * @extends L.Control.MiniMap
 * @module Widgets
 * @constructor
 * @param {Object} [options] Options to be passed to the MiniMap
 * @param {String} [options.position="bottomright"] The standard Leaflet.Control position parameter,
 *     used like all the other controls. Defaults to 'bottomright'.
 * @param {Number} [options.width=150] The width of the minimap in pixels
 * @param {Number} [options.height=150] The height of the minimap in pixels
 * @param {Number} [options.collapsedWidth=19] The width in pixels of the toggle marker and the minimap
 *     when collapsed
 * @param {Number} [options.collapsedHeight=19] The height in pixels of the toggle marker and the minimap
 *     when collapsed
 * @param {Number} [options.zoomLevelOffset=-8] The offset applied to the zoom in the minimap compared
 *     to the zoom of the main map. Can be positive or negative.
 * @param {Number} [options.zoomLevelFixed=null] Overrides the offset to apply a fixed zoom level
 *     to the minimap regardless of the main map zoom. Set it to any valid zoom level.
 *     If unset `zoomLevelOffset` is used instead.
 * @param {Boolean} [options.centerFixed=false] Applies a fixed position to the minimap regardless
 *     of the main map's view / position. Prevents panning the minimap, but does allow zooming
 *     (both in the minimap and the main map). If the minimap is zoomed, it will always
 *     zoom around the centerFixed point. You can pass in a LatLng-equivalent object.
 * @param {Boolean} [options.zoomAnimation=false] Sets whether the minimap should have an animated zoom.
 *     (Will cause it to lag a bit after the movement of the main map.)
 * @param {Boolean} [options.toggleDisplay=false] Sets whether the minimap should have a button to minimize it
 * @param {Boolean} [options.autoToggleDisplay=false] Sets whether the minimap should hide automatically
 *     if the parent map bounds does not fit within the minimap bounds.
 *     Especially useful when `zoomLevelFixed` is set.
 * @param {Boolean} [options.minimized=false] Sets whether the minimap should start in a minimized position
 * @param {Object} [options.aimingRectOptions] Sets the style of the aiming rectangle by passing in
 *     a <a href="http://leafletjs.com/reference-${leaflet.version}.html#path-options">Path.Options object</a>.
 *     (Clickable will always be overridden and set to `false`).
 * @param {Object} [options.shadowRectOptions] Sets the style of the aiming shadow rectangle by passing in a
 *     <a href="http://leafletjs.com/reference-${leaflet.version}.html#path-options">Path.Options object</a>.
 *     (Clickable will always be overridden and set to `false`).
 * @param {Object} [options.strings] Overrides the default strings allowing for translation
 * @param {Object} [options.mapOptions] Sets Leaflet options for the MiniMap.
 *     It does not override the MiniMap default map options but extends them.
 *     See regular map options for more details.
 * @param {String} [options.basePath = '/sdk'] Base path for sdk. It will be used in relative paths - it is 
 * especially important for maps with vector sources. For instance, if you set this option to `"/myPath"`, 
 * default urls for styles will be set to `/myPath/styles/basic_main.json`
 */
/* jshint nomen:false */
define(['display/map', 'leaflet-minimap'],
    function(L, MiniMap) {
        'use strict';
        var MAP_LAYER_DEFAULT_OPTIONS = {
                zoom: 2,
                minZoom: 2,
                maxZoom: 10
            },
            CONTROL_DEFAULT_OPTIONS = {
                toggleDisplay: true,
                zoomAnimation: true,
                autoToggleDisplay: true,
                zoomLevelOffset: -5
            };

        L.TomTomMinimap = MiniMap.extend({
            /**
             * Returns current zoom level of the mini map.
             *
             * @method getZoom
             * @return {Number}
             */
            getZoom: function() {
                return this._miniMap.getZoom();
            },
            /**
             * Add listeners for a particular event type. Event handlers for events with 'map-' prefix
             * are added to minimap map instance directly.
             *
             * @method on
             * @param {String} eventName
             * @param {Function} eventHandler
             * @return {Object} Returns reference to current instance of minimap widget
             */
            on: function(eventName, eventHandler) {
                var regexMatch, mapEventName;
                if (!eventName || !eventHandler) {
                    return this;
                }
                regexMatch = /^map-(.+)$/.exec(eventName);
                if (regexMatch) {
                    mapEventName = regexMatch[1];
                    this._miniMap.on(mapEventName, eventHandler);
                }
                return this;
            },
            _getMinimapBaseLayer: function(source) {
                return this._miniMap.getBaseLayers()[source || this.options.mapOptions.source[0]];
            },
            /**
             * Change a source data for the current mini map. It should be one of the sources provided
             * at creation in a property <b>options.mapOptions.source</b>.
             *
             * @method setMapSource
             * @param {String} source A source of data for the mini map. It has to be one of the
             *     {{#crossLink "L.Map"}}{{/crossLink}} supported sources.
             */
            setMapSource: function(source) {
                this._miniMap.removeLayer(this._layer);
                this._miniMap.setMapSource(source);
                this._layer = this._getMinimapBaseLayer(source);
            },
            initialize: function(options) {
                /*
                   This `dummyLayer` requires few words of explanation. Although it doesn't look like it this is the
                   cleanest way to move decision about layer configuration from this file to map.js instance created
                   inside MiniMap.onAdd method. Unfortunately, MiniMap plugin requires that a layer needs to be provided
                   that's the reason behind this `dummyLayer`. This change was caused by LNS-16272.
                 */
                var dummyLayer = new L.LayerGroup();
                MiniMap.prototype.initialize.call(this, dummyLayer, options);
            }
        });

        return function(options) {

            if (!options) {
                options = {};
            }

            options.mapOptions = L.extend(MAP_LAYER_DEFAULT_OPTIONS, options.mapOptions);
            options = L.extend(CONTROL_DEFAULT_OPTIONS, options);

            if (options.mapOptions.minZoom > options.mapOptions.maxZoom) {
                options.mapOptions.minZoom = options.mapOptions.maxZoom;
            }

            return new L.TomTomMinimap(options);
        };
    });



// WEBPACK FOOTER //
// ./widgets/views/miniMap.js